import React, { useMemo } from 'react';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import DetailModal, { DetailModalProps } from 'components/modals/DetailModal';
import { DetailCardType } from 'types/DetailPageTypes';
import { HeaderButtonsConfig, SingleHeaderButton } from 'types/ButtonTypes';
import ContactForm from '../../views/contacts/ContactForm';
import PersonForm from '../../views/contacts/PersonForm';
import AssetForm from '../../views/assets/AssetForm';
import LocationForm from '../../views/contacts/LocationForm';
import { useSettings } from 'services/settings/SettingsContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useFetchData } from 'services/api/useFetchData';
import { Division } from '../environments/EnvironmentTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import ProjectModal from './ProjectModal';

// Function to determine the client field link url based on its entity type
const determineLinkUrl = (client: any) => {
    if (client && client['entity_type'] && client['id']) {
        const baseUrl = client['entity_type'] === 'organization' ? '/contacts/organization' : '/contacts/person';
        return `${baseUrl}/${client['id']}`;
    }
    return null;
}

// Rename the name of the function:
const JobModal: React.FC<DetailModalProps> = ({ viewKey, itemId, refetchList }) => {

    // Determine whether equipment can be scheduled on the appointments
    const equipmentSchedulingActive = useActiveFeature('equipment_scheduling');
    const { environmentSettings } = useSettings();
    const { activeFeatures } = useAuthContext();
    const { initializeModal } = useModal();

    // Open the linked project in its modal on top of the job modal
    const openProjectModal = (project: { id: number }) => {
        if (!project?.id) return;
        initializeModal(<ProjectModal itemId={project.id} />, { modalSize: 'large-without-sidetab', itemId: project.id });
    };

    // Specify the parameters of this detail modal:
    const apiObject = 'job';
    const object = 'job';
    const showStatusBar = true;
    const showSummary = true;

    // Fetch the divisions, to group and restrict the job type options
    const hasDivisionsFeature = activeFeatures.includes('divisions');
    const { response: divisionsResponse } = useFetchData({
        apiUrl: hasDivisionsFeature ? 'get_division_list' : '',
        params: useMemo(() => ({ is_active: true }), []),
    });
    const divisions: Division[] = divisionsResponse?.results ?? [];

    // Define the header buttons of the modal:
    const headerButtons: HeaderButtonsConfig = {
        leftButtons: [
            {   
                type: 'secondary',
                label: 'button.download_pdf_button_label',
                action: 'download_pdf',
                objectType: 'job',
                allowedRight: ['can_download'],
                query: {
                    endpoint: 'get_pdf_layout_list/job',
                    object: 'pdf_layout',
                    defaultParams: { 'deleted': false },
                    useCache: true
                }
            },
        // {   
        //     type: 'secondary',
        //     label: 'button.send_email_button_label',
        //     action: 'send_email',
        // },
            {   
                type: 'dropdown',
                label: 'button.more_button_label',
                buttons: [
                    // {
                    //     label: 'button.convert_to_project_button_label',
                    //     action: 'convert_project',
                    //     restrictedRight: ['only_view'],
                    // },
                    // {
                    //     label: 'button.convert_to_proposal_button_label',
                    //     action: 'convert_proposal',
                    //     restrictedRight: ['only_view'],
                    //     allowedFeature: ['proposals'],
                    // },
                    {
                        label: 'button.send_sms_button_label',
                        action: 'send_sms',
                        restrictedRight: ['only_view'],
                        allowedFeature: ['sms_reminders'],
                    },
                    {
                        label: 'button.reopen_verified_job_button_label',
                        action: 'reopen',
                        restrictedRight: ['only_view'],
                        allowedRight: ['change_settings'],
                        condition: [{ field: 'status_condition', values: ['finished'] }]
                    },
                    // {
                    //     label: 'button.duplicate_job_button_label',
                    //     action: 'duplicate',
                    //     restrictedRight: ['only_view'],
                    // },
                    {
                        label: 'button.create_purchase_button_label',
                        action: 'create_purchase',
                        restrictedRight: ['only_view'],
                        allowedFeature: ['purchases'],
                    },
                    {
                        label: 'button.delete_job_button_label',
                        action: 'delete',
                        onSuccess: 'closeModal',
                        restrictedRight: ['only_view'],
                        allowedRight: ['can_delete'],
                    }
                ]
            },
        ],
        rightButtons: [
            {   
                type: 'primary',
                label: 'button.schedule_button_label',
                action: 'schedule',
                restrictedRight: ['only_view'],
                allowedFeature: ['scheduling'],
                customClass: 'scheduled_appointment',
                condition: [{ field: 'status_condition', values: ['all_purchases_delivered'] }]
            },
            {   
                type: 'primary',
                label: 'button.reschedule_button_label',
                action: 'schedule',
                restrictedRight: ['only_view'],
                allowedFeature: ['scheduling'],
                customClass: 'scheduled_appointment',
                condition: [{ field: 'status_condition', values: ['incomplete'] }]
            },
            {
                type: 'icon',
                icon: <FontAwesomeIcon icon={faPaperPlane} />,
                label: 'button.copy_workorder_button_label',
                objectType: 'jobs',
                action: 'send_email',
                actionValue: 'job_workorder_copy',
                restrictedRight: ['only_view'],
                condition: [
                    { field: 'status_condition', values: ['completed', 'finished'] }
                ]
            },
            {   
                type: 'primary',
                label: 'button.verify_job_button_label',
                action: 'verify',
                restrictedRight: ['only_view'],
                customClass: 'finished',
                condition: [{ field: 'status_condition', values: ['completed'] }]
            },
            {   // Jobs may only be invoiced when verified
                type: 'primary',
                label: 'button.create_invoice_button_label',
                action: 'create_invoice',
                restrictedRight: ['only_view'],
                allowedFeature: ['invoices'],
                query: {
                    endpoint: 'get_workflow_for_feature_list/invoice',
                    object: 'invoice_workflow',
                    defaultParams: { is_active: true },
                },
                condition: [
                    { field: 'status_condition', values: ['finished'] },
                ]
            },
            // Render the normal scheduling button
            ...(environmentSettings.scheduling_manual_to_schedule_status === false ? [
                {
                    type: 'primary',
                    label: 'button.schedule_button_label',
                    action: 'schedule',
                    restrictedRight: ['only_view'],
                    allowedFeature: ['scheduling'],
                    customClass: 'scheduled_appointment',
                    condition: [{ field: 'status_condition', values: ['job_created'] }]
                } as SingleHeaderButton
            // Render the manual to schedule button
            ] : [
                {
                    type: 'primary',
                    label: 'button.set_to_schedule_button_label',
                    action: 'set_to_schedule',
                    restrictedRight: ['only_view'],
                    allowedFeature: ['scheduling'],
                    customClass: 'job_to_be_scheduled',
                    condition: [{ field: 'status_condition', values: ['job_created'] }]
                },
                {
                    type: 'primary',
                    label: 'button.schedule_button_label',
                    action: 'schedule',
                    restrictedRight: ['only_view'],
                    allowedFeature: ['scheduling'],
                    customClass: 'scheduled_appointment',
                    condition: [{ field: 'status_condition', values: ['to_schedule'] }]
                }
            ] as SingleHeaderButton[]),
        ]
    }

    // Hide a section for finished jobs when the given data fields have no content
    const hideWhenFinishedAndEmpty = (dataFields: string[]) => ({
        dataCondition: [{ field: 'status_condition', values: ['finished'] }],
        dataFields: dataFields,
    });

    // Define the sections of the modal
    const sections: DetailCardType[] = [
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'job.general.client_card_header',
                    fields: [
                        { 
                            type: 'searchselect', 
                            name: 'client',
                            label: 'job.general.client_label',
                            placeholder: 'job.general.client_placeholder',
                            objectName: 'client.general.object_name.singular',
                            query: {
                                // Get all contacts which have the contact type 'contact', 'lead' and 'client'
                                endpoint: 'get_contact_search',
                                params: {
                                    contact_type: ['contact', 'lead', 'client'] 
                                }
                            },
                            viewModeLinkUrl: { function: determineLinkUrl },
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                                subtitle: { field: 'primary_location', format: (value) => value.address, optional: true},
                            },
                            addNewItem: { method: 'modal-form', modalForm: <ContactForm /> }
                        },
                        { 
                            type: 'searchselect', 
                            name: 'reporting_person',
                            label: 'job.general.reporting_person_label',
                            placeholder: 'job.general.reporting_person_placeholder',
                            objectName: 'person.general.object_name.singular',
                            query: { endpoint: 'get_person_search' },
                            showOptionsOnClick: (updatedData) => {
                                const clientId = updatedData?.client?.id ?? updatedData?.client;
                                if (!clientId) return null;
                                return { endpoint: `get_person_search/${clientId}` };
                            },
                            directOptionsTitle: 'job.general.persons_of_client_label',
                            dataCondition: [{'client.entity_type': 'organization'}],
                            viewModeLinkUrl: { baseUrl: '/contacts/person' },
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                            },
                            // tooltipText: 'job.general.reporting_person_tooltipText',
                            addNewItem: { method: 'modal-form', modalForm: <PersonForm /> }
                        },
                        { 
                            type: 'searchselect', 
                            name: 'invoice_address',
                            label: 'job.general.invoice_address_label',
                            placeholder: 'job.general.invoice_address_placeholder',
                            objectName: 'location.general.object_name.singular',
                            query: { endpoint: 'get_location_search' },
                            showOptionsOnClick: (updatedData) => {
                                const clientId = updatedData?.client?.id ?? updatedData?.client;
                                if (!clientId) return null;
                                return { endpoint: `get_location_search/${clientId}` };
                            },
                            directOptionsTitle: 'job.general.locations_of_client_label',
                            dataCondition: [{'client.entity_type': 'organization'}],
                            viewModeLinkUrl: { baseUrl: '/contacts/work-locations' },
                            selectionFormat: 'address',
                            optionFormat: {
                                title: { field: 'address', format: (value) => value },
                            },
                            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> }
                        },
                    ]
                },
                {
                    title: 'job.general.work_location_card_header',
                    fields: [
                        { 
                            type: 'searchselect', 
                            name: 'work_location',
                            label: 'job.general.work_location_label',
                            placeholder: 'job.general.work_location_placeholder',
                            objectName: 'location.general.object_name.singular',
                            query: {
                                // Get all locations
                                endpoint: 'get_location_search',
                                params: {
                                }
                            },
                            viewModeLinkUrl: { baseUrl: '/contacts/work-locations' },
                            selectionFormat: 'address',
                            optionFormat: {
                                title: { field: 'address', format: (value) => value },
                            },
                            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> }
                        },
                        {
                            type: 'multi-searchselect',
                            name: 'assets',
                            label: 'job.general.assets_label',
                            placeholder: 'job.general.assets_placeholder',
                            tooltipText: 'job.general.assets_tooltip',
                            objectName: 'asset.general.object_name.singular',
                            activeFeature: 'asset_management',
                            query: (updatedData) => {
                                const workLocationId = updatedData?.work_location?.id ?? updatedData?.work_location;
                                if (workLocationId) {
                                    return { endpoint: `get_asset_search/${workLocationId}` };
                                }
                                return { endpoint: 'get_asset_search' };
                            },
                            showOptionsOnClick: (updatedData) => {
                                const workLocationId = updatedData?.work_location?.id ?? updatedData?.work_location;
                                if (!workLocationId) return null;
                                return { endpoint: `get_asset_search/${workLocationId}` };
                            },
                            directOptionsTitle: 'asset.general.assets_of_location_label',
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                                subtitle: { field: 'number', format: (value) => value, optional: true },
                            },
                            specialOptions: [
                                { value: 'all', label: 'asset.general.all_assets_option', dataField: 'all_assets' },
                            ],
                            addNewItem: {
                                method: 'modal-form',
                                modalForm: <AssetForm />,
                                linkedItem: (updatedData) => {
                                    const workLocationId = updatedData?.work_location?.id ?? updatedData?.work_location;
                                    return workLocationId ? { location: workLocationId } : undefined;
                                },
                            },
                        },
                        {
                            type: 'searchselect',
                            name: 'location_person',
                            label: 'job.general.contact_at_location_label',
                            placeholder: 'job.general.contact_at_location_placeholder',
                            objectName: 'person.general.object_name.singular',
                            query: { endpoint: 'get_person_search' },
                            showOptionsOnClick: (updatedData) => {
                                const clientId = updatedData?.client?.id ?? updatedData?.client;
                                if (!clientId) return null;
                                return { endpoint: `get_person_search/${clientId}` };
                            },
                            directOptionsTitle: 'job.general.persons_of_client_label',
                            dataCondition: [{'client.entity_type': 'organization'}],
                            viewModeLinkUrl: { baseUrl: '/contacts/person' },
                            selectionFormat: 'name',
                            optionFormat: { title: { field: 'name', format: (value) => value } },
                            addNewItem: { method: 'modal-form', modalForm: <PersonForm /> }
                        },
                    ]
                }
            ]
        },
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'job.general.detail_card_header',
                    fields: [
                        {
                            type: 'dropdown',
                            name: 'workflow',
                            label: 'job.general.type_label',
                            apiRequest: [{
                                endpoint: 'get_workflow_list',
                                object: 'workflow',
                                params: { is_active: true, connected_feature: 'job' },
                                useCache: true
                            }],
                            selectionFormat: 'name',
                            optionFormat: 'name',
                            orderOptions: true,
                            backendField: 'id',
                            disabled_selected: 'job.general.type_disabled_selected',
                            activeFeature: 'job_types',
                            allowNoneOption: false,
                            showSearch: false,
                            groupByField: 'division',
                            formatGroupTitle: (group) => divisions.find(division => String(division.id) === group)?.name || group,
                            restrictGroupToField: 'division',
                        },
                        {
                            type: 'searchselect', 
                            name: 'project',
                            label: 'job.general.project_label',
                            placeholder: 'job.general.project_placeholder',
                            objectName: 'project.general.object_name.singular',
                            query: { endpoint: 'get_project_search' },
                            disableAddNew: true,
                            viewModeLinkClick: openProjectModal,
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                                subtitle: { field: 'number', format: (value) => value, optional: true},
                            },
                            activeFeature: 'projects',
                        },
                        { 
                            type: 'reference', 
                            name: 'reference',
                            label: 'job.general.reference_label',
                            tooltipText: 'job.general.reference_tooltip',
                        },
                        { 
                            type: 'dropdown', 
                            name: 'billing_type', 
                            label: 'job.general.billing_type_label',
                            disabled_selected: 'forms.general_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                            hardcodedOptions: [
                                { value: 'calculation', name: 'job.billing_type.calculation'}, 
                                { value: 'fixed', name: 'job.billing_type.fixed'}, 
                                { value: 'non_billing', name: 'job.billing_type.non_billing'}, 
                            ],
                        },
                        { 
                            type: 'price-currency', 
                            name: 'budget',
                            label: 'job.general.budget_label',
                            apiObject: 'currency',
                            dataCondition: [{'billing_type': 'fixed'}],
                        },
                    ],
                },
                {
                    fields: [
                        { 
                            type: 'dropdown', 
                            name: 'division', 
                            label: 'job.general.division_label', 
                            apiRequest: [{
                                endpoint: 'get_division_list',
                                object: 'division',
                                params: { is_active: true },
                                useCache: true
                            }],
                            activeFeature: 'divisions',
                            disabled_selected: 'job.general.division_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                        { 
                            type: 'dropdown', 
                            name: 'managed_by_user', 
                            label: 'job.general.managed_by_user_label', 
                            apiRequest: [{
                                endpoint: 'get_user_list',
                                object: 'office_user',
                                params: { is_active: true, office_access: true },
                                useCache: true
                            }],
                            disabled_selected: 'job.general.managed_by_user_disabled_selected',
                            optionFormat: 'full_name',
                            selectionFormat: 'full_name',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                        { 
                            type: 'multiselect', 
                            name: 'labels', 
                            label: 'job.general.labels_label', 
                            apiObject: 'label',
                            apiRequest: [{
                                endpoint: 'get_label_list',
                                object: 'label',
                                params: { deleted: false, connected_feature: 'jobs' },
                                useCache: true
                            }],
                            allowNoneOption: false,
                            showSearch: false,
                            disabled_selected: 'job.general.labels_disabled_selected',
                        },
                        {
                            type: 'target-date', 
                            name: 'target_date', 
                            label: 'job.general.target_date_label'
                        },
                    ]
                }
            ]
        },
        {
            component: 'FieldsCard',
            title: 'job.general.additional_information_header',
            alignment: 'horizontal',
            fields: [
                {
                    type: 'custom-fields',
                    name: 'custom_fields',
                }
            ]
        },
        {
            component: 'FieldsCard',
            title: 'job.general.activities_header',
            alignment: 'stacked',
            hideWhenEmpty: hideWhenFinishedAndEmpty(['main_description', 'tasks', 'descriptions']),
            fields: [
                {
                    type: 'textarea',
                    name: 'main_description',
                    emptyLabel: 'job.general.main_description_emptyLabel',
                    placeholder: 'job.general.main_description_placeholder',
                    enableStyling: 'extended',
                    showToolbarInitially: true,
                    rows: 2,
                },
            ]
        },
        {
            component: 'FieldsCard',
            title: 'job.general.tasks_header',
            alignment: 'stacked',
            hideWhenEmpty: hideWhenFinishedAndEmpty(['tasks']),
            fields: [
                {
                    type: 'tasks',
                    name: 'tasks',
                    apiRequest: [{
                        endpoint: 'get_form_list',
                        object: 'form',
                        params: { 'deleted': false },
                        useCache: true
                    }]
                },
            ]
        },
        {
            component: 'FieldsCard',
            title: 'job.general.attachments_header',
            hideWhenEmpty: hideWhenFinishedAndEmpty(['attachments']),
            fields: [
                {
                    type: 'attachments',
                    name: 'attachments',
                    apiRequest: [{
                        endpoint: 'get_attachmenttype_list',
                        object: 'attachmenttype',
                        params: { 'deleted': false },
                        useCache: true
                    }]
                }
            ]
        },
        {
            component: 'FieldsCard',
            title: 'job.general.appointments_header',
            fields: [
                {
                    name: 'appointments',
                    type: 'appointments',
                    apiRequest: [
                        {
                            endpoint: 'get_user_list',
                            object: 'user',
                            params: { is_active: true, field_access: true },
                            useCache: true
                        },
                        // Only load the equipment when it can be scheduled in this environment
                        ...(equipmentSchedulingActive ? [{
                            endpoint: 'get_equipment_list',
                            object: 'equipment',
                            params: { is_active: true },
                            useCache: true
                        }] : [])
                    ]
                },
            ]
        },
        {
            component: 'FieldsCard',
            title: 'job.general.executed_work_header',
            alignment: 'stacked',
            dataCondition: [{ field: 'descriptions', notEmpty: true }],
            fields: [
                {
                    type: 'job-activities',
                    name: 'descriptions'
                },
            ]
        },
        {
            component: 'FieldsCard',
            title: 'job.items.articles_label',
            hideWhenEmpty: hideWhenFinishedAndEmpty(['items']),
            fields: [
                { 
                    name: 'items',
                    type: 'job-items',
                    jobId: itemId,
                    apiRequest: [{
                        endpoint: 'get_vatrate_list',
                        object: 'vatrate',
                        params: { is_active: true },
                        useCache: true
                    }]
                }
            ]
        },
        {
            component: 'FieldsCard',
            title: 'job.timerows.timerows_label',
            hideWhenEmpty: hideWhenFinishedAndEmpty(['timerows']),
            fields: [
                { 
                    name: 'timerows',
                    type: 'timerows',
                    jobId: itemId,
                    apiRequest: [
                        {
                            endpoint: 'get_timetype_list',
                            object: 'timetype',
                            params: { is_active: true, type: 'productive' },
                            useCache: true
                        },
                        {
                            endpoint: 'get_hourlyrate_list',
                            object: 'hourlyrate',
                            params: { is_active: true },
                            useCache: true
                        },
                        {
                            endpoint: 'get_user_list',
                            object: 'field_user',
                            params: { is_active: true, field_access: true },
                            useCache: true
                        },
                        // Only load the equipment when it can be scheduled in this environment
                        ...(equipmentSchedulingActive ? [{
                            endpoint: 'get_equipment_list',
                            object: 'equipment',
                            params: { is_active: true },
                            useCache: true
                        }] : [])
                    ]                 
                }
            ]
        },
        {
            component: 'PhotosCard'
        },
        {
            component: 'SignaturesSection'
        },
    ]
    
    return (
        <DetailModal 
            viewKey={viewKey}
            objectType={object}
            apiObject={apiObject}
            itemId={itemId}
            headerButtons={headerButtons}
            statusBar={showStatusBar}
            sections={sections}
            summary={showSummary}
            refetchList={refetchList}
        />
    );
};

export default JobModal;